import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { SheetRow, SheetStatus } from '../../../api/types';
import { Badge, Button, Initials, PagedFooter, SortableTh, TextLink, TH_CLASS } from '../../../components/ui';
import usePagination from '../../../lib/usePagination';
import { useSortableTable, type SortColumn } from '../../../lib/useSortableTable';
import { warningLabel } from './scanWarnings';
import { isReviewable } from '../sheetStats';
import { PASS_MARK } from '../../../lib/tableRules';
import { formatRoll } from '../../../lib/roll';

// Default row order (plan B Task 5): needs review, duplicate and error first
// (they need a human), then matched, then still-processing, then the ones
// already dealt with. Ties break on roll number.
const STATUS_PRIORITY: Record<SheetStatus, number> = {
  needs_review: 0, duplicate: 1, error: 2, matched: 3, queued: 4, reading: 4, confirmed: 5, discarded: 6,
};
const STATUS_LABEL: Record<SheetStatus, string> = {
  queued: 'Queued', reading: 'Reading', matched: 'Matched', needs_review: 'Needs review',
  duplicate: 'Duplicate', error: 'Error', confirmed: 'Confirmed', discarded: 'Discarded',
};
type BadgeTone = 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'accent';
// Design pills: confirmed p-ok, matched p-info, needs review p-acc, duplicate p-warn, error p-crit, the rest p-mute.
const STATUS_TONE: Record<SheetStatus, BadgeTone> = {
  matched: 'info', needs_review: 'accent', duplicate: 'warning', error: 'danger',
  confirmed: 'success', discarded: 'neutral', queued: 'neutral', reading: 'neutral',
};

const TD = 'border-b border-hairline px-2.5 py-3 align-middle group-last:border-b-0';

interface ScansTableProps {
  rows: SheetRow[];
  /** Changing this (e.g. the status filter) returns the table to page 1. */
  resetKey?: unknown;
  /** Opens the sheet in the review workspace. */
  onReview: (row: SheetRow) => void;
  /** A student's section label ("10 · A"), for the sub line under the name. */
  sectionOf?: (studentId: number) => string | null;
}

export default function ScansTable({ rows, resetKey, onReview, sectionOf }: ScansTableProps) {
  const columns = useMemo<SortColumn<SheetRow>[]>(() => [
    {
      key: 'priority',
      accessor: (r) => STATUS_PRIORITY[r.status] * 1_000_000 + (r.roll_number_read ?? 999_999),
      type: 'number',
    },
    { key: 'roll', accessor: (r) => r.roll_number_read ?? undefined, type: 'number' },
    { key: 'student', accessor: (r) => r.student?.name || undefined },
    { key: 'status', accessor: (r) => STATUS_LABEL[r.status] },
    { key: 'answered', accessor: (r) => r.answered, type: 'number' },
    { key: 'score', accessor: (r) => (r.score_preview ? r.score_preview.right : undefined), type: 'number' },
  ], []);
  const { rows: sortedRows, sort, toggle } = useSortableTable(rows, columns, { key: 'priority', dir: 'asc' });
  const pg = usePagination(sortedRows, 10, `${String(resetKey)}|${sort?.key}|${sort?.dir}`);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[920px] border-collapse whitespace-nowrap text-left text-sm">
        <thead>
          <tr>
            <SortableTh columnKey="student" sort={sort} onToggle={toggle} className={TH_CLASS}>Student</SortableTh>
            <SortableTh columnKey="roll" sort={sort} onToggle={toggle} className={TH_CLASS} help="roll_number">Roll</SortableTh>
            <SortableTh columnKey="status" sort={sort} onToggle={toggle} className={TH_CLASS} help="sheet_status">Status</SortableTh>
            <SortableTh columnKey="answered" sort={sort} onToggle={toggle} className={TH_CLASS} align="right">Answered / blank / multi</SortableTh>
            <SortableTh columnKey="score" sort={sort} onToggle={toggle} className={TH_CLASS} align="right">Score</SortableTh>
            <th scope="col" className={TH_CLASS}>Warnings</th>
            <th scope="col" className={`${TH_CLASS} text-right`}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {pg.pageItems.map((row) => {
            const section = row.student ? sectionOf?.(row.student.id) ?? null : null;
            const sp = row.score_preview;
            const pct = sp && sp.total > 0 ? Math.round((sp.right / sp.total) * 1000) / 10 : null;
            const low = pct != null && pct < PASS_MARK;
            const sub = [section, `sheet #${row.id}`].filter(Boolean).join(' · ');
            return (
              <tr key={row.id} className="group transition-colors [&:hover>td]:bg-surface-2">
                <td className={TD}>
                  {row.student ? (
                    <Link to={`/students/profile/${row.student.id}`} className="flex items-center gap-2.5 no-underline">
                      <Initials name={row.student.name} />
                      <span>
                        <span className="block font-semibold text-ink">{row.student.name}</span>
                        <span className="text-xs text-muted">{sub}</span>
                      </span>
                    </Link>
                  ) : (
                    <span className="flex items-center gap-2.5">
                      <span aria-hidden className="inline-grid h-9 w-9 flex-none place-items-center rounded-[12px] bg-accent-bg text-xs font-semibold text-accent-ink">?</span>
                      <span>
                        <span className="block font-semibold text-accent-ink">Unknown student</span>
                        <span className="text-xs text-muted">sheet #{row.id}</span>
                      </span>
                    </span>
                  )}
                </td>
                <td className={`${TD} font-mono font-medium tabular-nums text-ink`}>
                  {formatRoll(row.roll_number_read)}
                </td>
                <td className={TD}><Badge tone={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</Badge></td>
                <td className={`${TD} text-right font-mono font-medium tabular-nums text-ink`}>
                  {row.answered} / {row.blank} / {row.multi}
                </td>
                <td className={`${TD} text-right`}>
                  {sp ? (
                    <>
                      <span className={`font-mono font-medium tabular-nums ${low ? 'text-accent-ink' : 'text-ink'}`}>{sp.right} / {sp.total}</span>
                      {pct != null && <span className="ml-1.5 font-mono text-xs font-medium text-muted">{pct}%</span>}
                    </>
                  ) : <span className="font-mono text-muted">—</span>}
                </td>
                <td className={TD}>
                  {row.warnings.length === 0 ? (
                    <span className="text-xs text-muted">—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {row.warnings.map((w) => (
                        <span key={w} className="inline-flex items-center whitespace-nowrap rounded-[8px] bg-warning-bg px-[9px] py-[3px] text-xs font-medium text-warning-ink">{warningLabel(w)}</span>
                      ))}
                    </div>
                  )}
                </td>
                <td className={`${TD} text-right`}>
                  <div className="flex items-center justify-end gap-2">
                    {(isReviewable(row.status) || row.status === 'matched') && (
                      <Button variant="ghost" size="sm" className="whitespace-nowrap" onClick={() => onReview(row)}>Review</Button>
                    )}
                    {!isReviewable(row.status) && row.student && (
                      <TextLink to={`/students/profile/${row.student.id}`}>Student 360 →</TextLink>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {sortedRows.length === 0 && (
            <tr>
              <td colSpan={7} className="px-3 py-6 text-center text-sm text-muted">No sheets match this filter.</td>
            </tr>
          )}
        </tbody>
      </table>
      <PagedFooter className="mt-4" from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} />
    </div>
  );
}
